import useSWR, { mutate } from "swr";
import { Utils } from "@/common/utils";
import { StandardResponse } from "@/types/swr";

const clientConfigs = Utils.getEAIClientConfigs();

/**
 * Revalidates every cached saved-views list so mounted panels reflect a change
 * made elsewhere (e.g. after creating a view in the Save View modal). Lists
 * that are not currently subscribed simply refetch the next time they mount.
 */
export function revalidateSavedViews() {
    return mutate(
        (key) => typeof key === "string" && key.includes("/saved_views")
    );
}

/**
 * A saved view: a named, shareable bookmark of a dashboard URL. Belongs to a
 * school and points at a dashboard. Access is membership-based — see
 * {@link SavedViewMember}.
 */
export interface SavedView {
    ruuid: string;
    school_id: string;
    dashboard_id: number;
    name: string;
    description: string | null;
    url: string;
    created_by_ruuid: string;
    enflux_created: string;
    enflux_updated: string;
}

/**
 * A saved view as returned by the list endpoint: the base view joined through
 * to its dashboard, plus who shared it with the current user.
 */
export interface SavedViewListItem extends SavedView {
    /** `null` for views the current user created; the sharer's ruuid otherwise. */
    shared_by_ruuid: string | null;
    dashboard_name: string;
    dashboard_thumbnail: string | null;
}

/** A membership row: who has access to a view and who shared it with them. */
export interface SavedViewMember {
    ruuid: string;
    saved_view_ruuid: string;
    user_ruuid: string;
    /** `null` for the creator's own membership; the sharer's ruuid otherwise. */
    shared_by_ruuid: string | null;
    enflux_created: string;
}

export interface CreateSavedViewPayload {
    dashboard_id: number;
    name: string;
    url: string;
    description?: string;
}

export interface UpdateSavedViewPayload {
    name?: string;
    url?: string;
    description?: string;
}

export interface ShareSavedViewPayload {
    user_ruuids: string[];
}

/** Returned by create / update / delete: a bare reference to the affected view. */
export interface SavedViewRef {
    ruuid: string;
}

/** Returned by share: the ruuids actually granted access (already-members omitted). */
export interface ShareSavedViewResult {
    added: string[];
}

/**
 * Lists every saved view the current user is a member of (their own + shared
 * with them), across all dashboards.
 *
 * @param dashboardId Optional — restrict the list to a single dashboard.
 * @param options.enabled Set to `false` to skip fetching (e.g. until a panel is opened).
 */
export function useSavedViews(dashboardId?: number | null, options?: { enabled?: boolean }) {
    const enabled = options?.enabled ?? true;
    const query = dashboardId != null ? `?dashboard_id=${dashboardId}` : "";
    const { data, error, isLoading, mutate } = useSWR<StandardResponse<SavedViewListItem[]>, Error>(
        enabled ? `${clientConfigs.baseUrl}/saved_views${query}` : null
    );

    return {
        data: data?.data,
        error,
        isLoading,
        mutate,
    };
}

/**
 * Fetches a single saved view. The current user must be a member; otherwise the
 * request fails with 403. Pass a falsy `viewRuuid` to skip fetching.
 */
export function useSavedView(viewRuuid?: string | null) {
    const { data, error, isLoading, mutate } = useSWR<StandardResponse<SavedView>, Error>(
        viewRuuid ? `${clientConfigs.baseUrl}/saved_views/${viewRuuid}` : null
    );

    return {
        data: data?.data,
        error,
        isLoading,
        mutate,
    };
}

/**
 * Lists the members of a saved view (who has access and who shared it with
 * them). The current user must be a member. Pass a falsy `viewRuuid` to skip
 * fetching.
 */
export function useSavedViewMembers(viewRuuid?: string | null) {
    const { data, error, isLoading, mutate } = useSWR<StandardResponse<SavedViewMember[]>, Error>(
        viewRuuid ? `${clientConfigs.baseUrl}/saved_views/${viewRuuid}/users` : null
    );

    return {
        data: data?.data,
        error,
        isLoading,
        mutate,
    };
}
